'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'next/navigation'
import { buildClient } from '@datocms/cma-client-browser'
import { useSiteSearch } from 'react-datocms'
import { cn } from '@desoutter/uikit/src/utils'
import { Search } from '@desoutter/uikit/src/components/icons'
import { InitView, NoResultsView, ResultsView } from '@/components/Search/index'
import { Loader } from '@desoutter/uikit'
import { LanguageRecord } from '@/graphql/types'
import { useSearch } from '@/hooks/useSearch'
import { useDebounce } from '@/hooks/useDebounce'
import { localeToDatoLocale } from '@/lib/locale.utils'
import { DatoLinkFragment } from '@/components/DatoLink/DatoLink.generated'

export function HeaderSearchBar({
	className,
	translations,
	contactPageLink,
}: {
	className?: string
	translations: LanguageRecord
	contactPageLink: DatoLinkFragment
}) {
	const { lang } = useParams()
	const datoLang = localeToDatoLocale(lang as string)
	const [isFocused, setFocused] = useState<boolean>(false)
	const [localQuery, setLocalQuery] = useState<string>('')

	const client = buildClient({
		// environment: 'develop',
		apiToken: process.env.NEXT_PUBLIC_DATOCMS_SITE_SEARCH_TOKEN,
	})

	// Debounce la requête de recherche avec un délai de 300ms
	const debouncedQuery = useDebounce(localQuery, 300)

	const { state, data } = useSiteSearch({
		client,
		fuzzySearch: true,
		initialState: { locale: lang as string },
		highlightMatch: (text, key) => <strong key={key + text}>{text}</strong>,
		buildTriggerId: '31315',
		resultsPerPage: 100,
	})

	const input = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (isFocused) input.current?.focus()
	}, [isFocused])

	useEffect(() => {
		state.setQuery(debouncedQuery)
	}, [debouncedQuery])

	const { loading, products, resources, dt } = useSearch(
		debouncedQuery,
		lang as string,
	)

	function close() {
		setFocused(false)
		setLocalQuery('')
	}

	const isResult = useMemo(() => {
		return (
			!loading &&
			(data?.pageResults?.filter((r) => r.url.includes(`/${datoLang}/`))
				.length || 0) +
				(debouncedQuery.length > 2 ? products.length : 0) +
				(dt ? 1 : 0) +
				resources.length
		)
	}, [data, products, dt, resources, debouncedQuery])

	const isLoading = useMemo(
		() => loading || (loading && (!data || !dt)),
		[loading, data, dt],
	)

	return (
		<div
			id="search-bar"
			className={cn('flex justify-end h-12 w-full ml-2', className)}
		>
			<div
				className="lg:bg-gray-900/10 lg:dark:bg-white/10 lg:relative w-min max-w-[150px] transition-[width] flex gap-2 justify-end lg:justify-between rounded-full p-1 lg:pl-4"
				onClick={() => setFocused(true)}
			>
				<div className="hidden lg:block cursor-pointer lg:w-full pt-2 bg-[transparent] focus-visible:outline-none text-foreground font-medium placeholder:text-gray-900 dark:placeholder:text-foreground z-10 whitespace-nowrap">
					{translations.searchLabel}
				</div>
				<button
					className="relative w-10 h-10 p-2 md:p-3 rounded-full lg:bg-white dark:lg:bg-gray-900 opacity-100 flex items-center justify-center z-10"
					title="search"
				>
					<Search className="stroke-gray-900 dark:stroke-white" />
				</button>
			</div>

			{isFocused && (
				<div
					className="fixed top-0 left-0 w-screen h-screen bg-black/50 pt-14 z-40 px-0 mx-0 flex justify-center items-start"
					onClick={() => setFocused(false)}
				>
					<div
						className="relative bg-white rounded-2xl p-2 w-[80vw] lg:w-[50vw] h-auto text-gray-900"
						style={{
							boxShadow: '0px 12px 24px 0px rgba(0, 0, 0, 0.08)',
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<button
							onFocus={close}
							className="absolute top-5 right-5 w-8 h-8 pb-1 rounded-full bg-gray-900 text-center z-40 flex items-center justify-center"
							title="close"
							key="search-btn-close"
						>
							<span className="text-white text-foreground text-xl leading-none font-normal">
								&times;
							</span>
						</button>
						<input
							ref={input}
							type="text"
							value={localQuery}
							onChange={(e) => setLocalQuery(e.target.value)}
							className="rounded-full m-1 p-1 py-3 pl-4 bg-gray-50 focus-visible:outline-none text-gray-900"
							style={{ width: 'calc(100% - 0.5rem)' }}
							placeholder={translations.searchPlaceholer}
						/>
						{(!localQuery || localQuery.length < 3) && (
							<InitView text={translations.searchBoxText} />
						)}
						{isLoading && <Loader />}
						{!isLoading &&
							debouncedQuery.length > 2 &&
							!isResult &&
							!loading && (
								<NoResultsView
									query={debouncedQuery}
									text={translations.noResult}
									noResultContact={translations.noResultContact}
									contactPageLink={contactPageLink}
									onClose={close}
								/>
							)}
						{!isLoading && debouncedQuery.length > 2 && !!isResult && (
							<ResultsView
								data={data?.pageResults.filter((d) =>
									d.url.includes(`/${datoLang}/`),
								)}
								products={debouncedQuery.length > 2 ? products : []}
								dt={dt}
								resources={resources}
								count={isResult}
								translations={translations}
							/>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
